/*
 * Copyright (c) 2026 Macrofocus GmbH. All Rights Reserved.
 */
package com.macrofocus.common.format

import com.macrofocus.common.math.big.CommonBigDecimal
import kotlin.math.abs
import kotlin.math.floor
import kotlin.math.pow
import kotlin.math.round
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/**
 * Formats written in common Kotlin, for the platforms without java.text (JavaScript and WebAssembly): the number
 * patterns of DecimalFormat and the date patterns of SimpleDateFormat, in English, with the defaults of the JVM
 * [FormatFactory].
 */
object PortableFormats {
    /** As DecimalFormat.getInstance(): grouping and up to 3 fraction digits. */
    fun decimalFormat(): CPFormat<Any?> = PatternNumberFormat("#,##0.###")

    /** As DecimalFormat.getInstance() with at most [fractionDigits] fraction digits, then [pre], [post], [rounding] and [factor]. */
    fun decimalFormat(fractionDigits: Int, pre: String?, post: String?, rounding: Double, factor: Double): CPFormat<Any?> =
        PatternNumberFormat("#,##0" + fractionPattern('#', fractionDigits), pre, post, rounding, factor)

    /** As DecimalFormat.getIntegerInstance(). */
    fun integerFormat(pre: String?, post: String?, rounding: Double): CPFormat<Any?> = PatternNumberFormat("#,##0", pre, post, rounding)

    /** As DecimalFormat.getPercentInstance() with at most [fractionDigits] fraction digits. */
    fun percentFormat(fractionDigits: Int, rounding: Double): CPFormat<Any?> =
        PatternNumberFormat("#,##0" + fractionPattern('#', fractionDigits) + "%", rounding = rounding)

    fun numberFormat(pattern: String?): CPFormat<Any?> = PatternNumberFormat(pattern ?: "#,##0.###")

    /**
     * A format of the SimpleDateFormat [pattern], formatting [Instant]s and milliseconds since the epoch in the time
     * zone [utcOffsetMinutes] gives at an instant (in milliseconds since the epoch).
     */
    fun dateFormat(pattern: String?, utcOffsetMinutes: (Long) -> Int = { 0 }): CPFormat<Any?> =
        PatternDateFormat(pattern ?: DATE, utcOffsetMinutes)

    /** The dates, with their time when it is not midnight, as FormatFactory.createDateOrDateTimeFormat. */
    fun dateOrDateTimeFormat(utcOffsetMinutes: (Long) -> Int = { 0 }): CPFormat<Any?> {
        val date = PatternDateFormat(DATE, utcOffsetMinutes)
        val dateTime = PatternDateFormat(DATE_TIME, utcOffsetMinutes)
        return object : AbstractFormat<Any?>() {
            override fun formatHtml(value: Any?, htmlSupported: Boolean): String? {
                val millis = PatternDateFormat.millis(value) ?: return value?.toString() ?: ""
                val local = millis + utcOffsetMinutes(millis) * 60_000L
                return if (floorMod(local, DAY) != 0L) dateTime.formatHtml(value, htmlSupported) else date.formatHtml(value, htmlSupported)
            }

            override fun parse(text: String?): Any? = try {
                dateTime.parse(text)
            } catch (e: CPFormat.ParsingException) {
                date.parse(text)
            }

            override val nativeFormat: Any?
                get() = null
        }
    }

    /** The patterns of the dates and of the dates with their time of the JVM [FormatFactory]. */
    const val DATE = "dd.MM.yyyy"
    const val DATE_TIME = "dd.MM.yyyy HH:mm:ss"

    private fun fractionPattern(digit: Char, count: Int): String = if (count > 0) "." + digit.toString().repeat(count) else ""
}

private const val DAY = 86_400_000L

private fun floorMod(a: Long, b: Long): Long = a.mod(b)

/**
 * The numbers of a DecimalFormat pattern such as `#,##0.00`, `0.0%` or `'CHF' #,##0.-`: the minimum (`0`) and maximum
 * (`0` and `#`) integer and fraction digits, grouping by thousands, a percent or per mille suffix, and the literal
 * prefix and suffix around the digits; the numbers are rounded half-even as by DecimalFormat. [pre], [post],
 * [rounding] and [factor] are those of the SwingFormat of the JVM [FormatFactory].
 */
class PatternNumberFormat(
    override val pattern: String?,
    private val pre: String? = null,
    private val post: String? = null,
    private val rounding: Double = 1.0,
    private val factor: Double = 1.0,
) : AbstractFormat<Any?>() {
    private val positive = (pattern ?: "#,##0.###").substringBefore(';')
    private val digitIndices = unquotedIndices(positive).filter { positive[it] in DIGIT_CHARS }
    private val digitsStart = digitIndices.firstOrNull() ?: positive.length
    private val digitsEnd = (digitIndices.lastOrNull() ?: (positive.length - 1)) + 1
    private val prefix = literal(positive.substring(0, digitsStart))
    private val suffixPattern = positive.substring(digitsEnd.coerceAtLeast(digitsStart))
    private val suffix = literal(suffixPattern)
    private val digits = positive.substring(digitsStart, digitsEnd.coerceAtLeast(digitsStart))
    private val integerDigits = digits.substringBefore('.')
    private val fraction = digits.substringAfter('.', "")
    private val minimumIntegerDigits = integerDigits.count { it == '0' }
    private val minimumFractionDigits = fraction.count { it == '0' }
    private val maximumFractionDigits = fraction.count { it == '0' || it == '#' }
    private val groupingSize = if (',' in integerDigits) integerDigits.length - integerDigits.lastIndexOf(',') - 1 else 0
    private val multiplier = when {
        '%' in suffixPattern.replace(Regex("'[^']*'"), "") -> 100.0
        '‰' in suffixPattern -> 1000.0
        else -> 1.0
    }

    override fun formatHtml(value: Any?, htmlSupported: Boolean): String? {
        var number = (value as? Number)?.toDouble() ?: return value?.toString()
        if (rounding != 1.0) {
            number = round(number / rounding) * rounding
        }
        number *= factor
        val text = formatNumber(number * multiplier)
        return (pre ?: "") + text + (post ?: "")
    }

    private fun formatNumber(number: Double): String {
        if (number.isNaN()) return "NaN"
        if (number.isInfinite()) return (if (number < 0) "-" else "") + prefix + "∞" + suffix
        val (integerText, rounded) = roundDigits(abs(number), maximumFractionDigits)
        var fractionText = rounded
        fractionText = fractionText.trimEnd('0').padEnd(minimumFractionDigits, '0')
        var integer = integerText.trimStart('0').padStart(minimumIntegerDigits, '0')
        if (groupingSize > 0) {
            integer = integer.reversed().chunked(groupingSize).joinToString(",").reversed()
        }
        // As DecimalFormat, also "-0" for the negative numbers rounded to zero
        val sign = if (number < 0 || number == 0.0 && 1 / number < 0) "-" else ""
        return sign + prefix + integer + (if (fractionText.isNotEmpty()) ".$fractionText" else "") + suffix
    }

    override fun parse(text: String?): Any? {
        if (text == null) return null
        var s = text.trim()
        pre?.let { s = s.removePrefix(it) }
        post?.let { s = s.removeSuffix(it) }
        s = s.trim()
        val negative = s.startsWith("-")
        s = s.removePrefix("-").removePrefix(prefix).removeSuffix(suffix).trim().replace(",", "").replace("'", "").replace(" ", "")
        val number = s.toDoubleOrNull() ?: throw CPFormat.ParsingException("Unparseable number: \"$text\"", 0)
        val value = (if (negative) -number else number) / multiplier / factor
        // As DecimalFormat.parse: a whole number is a Long
        return if (value == floor(value) && abs(value) < 9.0E18) value.toLong() else value
    }

    override val nativeFormat: Any?
        get() = null

    override val horizontalAlignment: CPFormat.HorizontalAlignment
        get() = CPFormat.HorizontalAlignment.Right

    private companion object {
        val DIGIT_CHARS = setOf('#', '0', ',', '.')

        /** The text of a prefix or suffix, without the quotes and the percent and per mille characters kept. */
        fun literal(text: String): String {
            val out = StringBuilder()
            var quoted = false
            var i = 0
            while (i < text.length) {
                val c = text[i]
                if (c == '\'') {
                    if (i + 1 < text.length && text[i + 1] == '\'') {
                        out.append('\'')
                        i++
                    } else {
                        quoted = !quoted
                    }
                } else {
                    out.append(c)
                }
                i++
            }
            return out.toString()
        }

        /**
         * The integer and fraction digits of [magnitude] rounded half-even to [fractionDigits], as DecimalFormat: from
         * the shortest decimal representation of the double, the ties being decided by its exact binary value.
         */
        fun roundDigits(magnitude: Double, fractionDigits: Int): Pair<String, String> {
            // The digits and the position of the decimal point of the shortest representation
            val text = magnitude.toString().lowercase()
            val mantissa = text.substringBefore('e')
            val exponent = if ('e' in text) text.substringAfter('e').removePrefix("+").toInt() else 0
            val integerDigits = mantissa.substringBefore('.')
            var digits = (integerDigits + mantissa.substringAfter('.', "")).trimStart('0')
            var point = integerDigits.trimStart('0').length + exponent
            if (digits.isEmpty()) {
                digits = "0"
                point = 1
            } else if (integerDigits.trimStart('0').isEmpty()) {
                // 0.00123: the leading zeros of the fraction are before the first digit
                point = exponent - (mantissa.substringAfter('.', "").length - mantissa.substringAfter('.', "").trimStart('0').length)
            }
            if (point < 0) {
                digits = "0".repeat(-point) + digits
                point = 0
            }
            val keep = point + fractionDigits
            var kept = digits.take(keep.coerceAtLeast(0)).padEnd(keep.coerceAtLeast(0), '0')
            if (digits.length > keep) {
                val dropped = digits.substring(keep.coerceAtLeast(0))
                val up = when {
                    dropped[0] > '5' -> true
                    dropped[0] < '5' -> false
                    dropped.drop(1).any { it != '0' } -> true
                    else -> {
                        // A tie of the decimal representation: the exact value of the double decides
                        val tie = (digits.substring(0, point) + "." + digits.substring(point)).let { if (it.startsWith(".")) "0$it" else it }
                        val comparison = CommonBigDecimal(magnitude).compareTo(CommonBigDecimal(tie))
                        if (comparison != 0) comparison > 0 else kept.lastOrNull()?.let { (it - '0') % 2 == 1 } ?: false
                    }
                }
                if (up) {
                    val chars = kept.toCharArray()
                    var i = chars.size - 1
                    while (i >= 0 && chars[i] == '9') {
                        chars[i] = '0'
                        i--
                    }
                    kept = if (i >= 0) {
                        chars[i] = chars[i] + 1
                        chars.concatToString()
                    } else {
                        point++
                        "1" + chars.concatToString()
                    }
                }
            }
            val integerPart = kept.substring(0, kept.length - fractionDigits).ifEmpty { "0" }
            return integerPart to kept.substring(kept.length - fractionDigits)
        }

        /** The indices of the characters of [text] outside of quotes. */
        fun unquotedIndices(text: String): List<Int> {
            val indices = ArrayList<Int>()
            var quoted = false
            for ((i, c) in text.withIndex()) {
                if (c == '\'') {
                    quoted = !quoted
                } else if (!quoted) {
                    indices.add(i)
                }
            }
            return indices
        }
    }
}

/**
 * The dates of a SimpleDateFormat pattern: `y`, `M` (`MMM` and `MMMM` the English names), `d`, `D`, `E`, `a`, `H`,
 * `k`, `K`, `h`, `m`, `s`, `S`, `Z`/`X` and quoted text. The values are [Instant]s or milliseconds since the epoch,
 * shown in the time zone given by [utcOffsetMinutes]; parsing gives [Instant]s.
 */
@OptIn(ExperimentalTime::class)
class PatternDateFormat(override val pattern: String, private val utcOffsetMinutes: (Long) -> Int = { 0 }) : AbstractFormat<Any?>() {
    private sealed class Token {
        class Field(val letter: Char, val count: Int) : Token()
        class Text(val text: String) : Token()
    }

    private val tokens: List<Token> = tokenize(pattern)

    override fun formatHtml(value: Any?, htmlSupported: Boolean): String? {
        val millis = millis(value) ?: return value?.toString() ?: ""
        val offset = utcOffsetMinutes(millis)
        val local = millis + offset * 60_000L
        val days = local.floorDiv(DAY)
        val msOfDay = floorMod(local, DAY)
        val (year, month, day) = civil(days)
        val hour = (msOfDay / 3_600_000).toInt()
        val minute = (msOfDay / 60_000 % 60).toInt()
        val second = (msOfDay / 1000 % 60).toInt()
        val milli = (msOfDay % 1000).toInt()
        val out = StringBuilder()
        for (token in tokens) {
            when (token) {
                is Token.Text -> out.append(token.text)
                is Token.Field -> {
                    val n = token.count
                    when (token.letter) {
                        'y', 'Y', 'u' -> out.append(if (n == 2) pad(year % 100, 2) else pad(year, n))
                        'M', 'L' -> out.append(
                            when {
                                n >= 4 -> MONTHS[month - 1]
                                n == 3 -> MONTHS[month - 1].take(3)
                                else -> pad(month, n)
                            }
                        )
                        'd' -> out.append(pad(day, n))
                        'D' -> out.append(pad((days - daysFromCivil(year, 1, 1) + 1).toInt(), n))
                        'E' -> {
                            val weekday = WEEKDAYS[floorMod(days + 3, 7).toInt()]
                            out.append(if (n >= 4) weekday else weekday.take(3))
                        }
                        'a' -> out.append(if (hour < 12) "AM" else "PM")
                        'H' -> out.append(pad(hour, n))
                        'k' -> out.append(pad(if (hour == 0) 24 else hour, n))
                        'K' -> out.append(pad(hour % 12, n))
                        'h' -> out.append(pad(if (hour % 12 == 0) 12 else hour % 12, n))
                        'm' -> out.append(pad(minute, n))
                        's' -> out.append(pad(second, n))
                        'S' -> out.append(pad(milli, 3).take(n).padEnd(n, '0'))
                        'Z', 'X' -> {
                            val sign = if (offset < 0) "-" else "+"
                            val hours = pad(abs(offset) / 60, 2)
                            val minutes = pad(abs(offset) % 60, 2)
                            out.append(if (token.letter == 'X' && offset == 0) "Z" else sign + hours + (if (token.letter == 'X' && n >= 3) ":" else "") + minutes)
                        }
                        else -> out.append(token.letter.toString().repeat(n))
                    }
                }
            }
        }
        return out.toString()
    }

    override fun parse(text: String?): Any? {
        if (text == null) return null
        var position = 0
        var year = 1970
        var month = 1
        var day = 1
        var hour = 0
        var minute = 0
        var second = 0
        var milli = 0
        var pm: Boolean? = null
        var offset: Int? = null

        fun fail(): Nothing = throw CPFormat.ParsingException("Unparseable date: \"$text\"", position)

        fun number(maxDigits: Int): Int {
            val start = position
            while (position < text.length && text[position].isDigit() && position - start < maxDigits) position++
            if (position == start) fail()
            return text.substring(start, position).toInt()
        }

        fun name(names: List<String>): Int {
            val index = names.indexOfFirst { text.regionMatches(position, it, 0, it.length, ignoreCase = true) }
            if (index < 0) fail()
            position += names[index].length
            return index
        }

        for ((i, token) in tokens.withIndex()) {
            when (token) {
                is Token.Text -> {
                    if (!text.regionMatches(position, token.text, 0, token.text.length, ignoreCase = true)) fail()
                    position += token.text.length
                }
                is Token.Field -> {
                    // Adjacent fields (yyyyMMdd) take only their own width
                    val next = tokens.getOrNull(i + 1)
                    val width = if (next is Token.Field) token.count else Int.MAX_VALUE
                    when (token.letter) {
                        'y', 'Y', 'u' -> {
                            val start = position
                            year = number(width)
                            if (token.count <= 2 && position - start <= 2) year += if (year < 50) 2000 else 1900
                        }
                        'M', 'L' -> month = if (token.count >= 3) name(MONTHS + MONTHS.map { it.take(3) }) % 12 + 1 else number(width)
                        'd' -> day = number(width)
                        'E' -> name(WEEKDAYS + WEEKDAYS.map { it.take(3) })
                        'a' -> pm = name(listOf("AM", "PM")) == 1
                        'H', 'k' -> hour = number(width) % 24
                        'K', 'h' -> hour = number(width) % 12
                        'm' -> minute = number(width)
                        's' -> second = number(width)
                        'S' -> milli = number(width)
                        'Z', 'X' -> {
                            if (position < text.length && text[position] == 'Z') {
                                position++
                                offset = 0
                            } else {
                                val sign = when (text.getOrNull(position)) {
                                    '+' -> 1
                                    '-' -> -1
                                    else -> fail()
                                }
                                position++
                                val hours = number(2)
                                if (text.getOrNull(position) == ':') position++
                                offset = sign * (hours * 60 + number(2))
                            }
                        }
                        else -> fail()
                    }
                }
            }
        }
        if (pm == true && hour < 12) hour += 12
        val local = daysFromCivil(year, month, day) * DAY + hour * 3_600_000L + minute * 60_000L + second * 1000L + milli
        // The offset of the local time, found from the offset at the same UTC time
        val millis = local - (offset ?: utcOffsetMinutes(local - utcOffsetMinutes(local) * 60_000L)) * 60_000L
        return Instant.fromEpochMilliseconds(millis)
    }

    override val nativeFormat: Any?
        get() = null

    companion object {
        private val MONTHS = listOf(
            "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December",
        )
        private val WEEKDAYS = listOf("Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday")

        /** The milliseconds since the epoch of an [Instant] or a number, else null. */
        fun millis(value: Any?): Long? = when (value) {
            is Instant -> value.toEpochMilliseconds()
            is Number -> value.toLong()
            else -> null
        }

        private fun tokenize(pattern: String): List<Token> {
            val tokens = ArrayList<Token>()
            val text = StringBuilder()
            var i = 0
            fun flush() {
                if (text.isNotEmpty()) {
                    tokens.add(Token.Text(text.toString()))
                    text.clear()
                }
            }
            while (i < pattern.length) {
                val c = pattern[i]
                when {
                    c == '\'' -> {
                        if (i + 1 < pattern.length && pattern[i + 1] == '\'') {
                            text.append('\'')
                            i += 2
                        } else {
                            val end = pattern.indexOf('\'', i + 1).let { if (it < 0) pattern.length else it }
                            text.append(pattern, i + 1, end)
                            i = end + 1
                        }
                    }
                    c.isLetter() && c.code < 128 -> {
                        flush()
                        var j = i
                        while (j < pattern.length && pattern[j] == c) j++
                        tokens.add(Token.Field(c, j - i))
                        i = j
                    }
                    else -> {
                        text.append(c)
                        i++
                    }
                }
            }
            flush()
            return tokens
        }

        private fun pad(value: Int, width: Int): String = value.toString().padStart(width, '0')

        /** The year, month and day of a number of days since the epoch (the algorithm of Howard Hinnant). */
        private fun civil(days: Long): Triple<Int, Int, Int> {
            val z = days + 719_468
            val era = (if (z >= 0) z else z - 146_096) / 146_097
            val doe = z - era * 146_097
            val yoe = (doe - doe / 1460 + doe / 36_524 - doe / 146_096) / 365
            val doy = doe - (365 * yoe + yoe / 4 - yoe / 100)
            val mp = (5 * doy + 2) / 153
            val d = (doy - (153 * mp + 2) / 5 + 1).toInt()
            val m = (if (mp < 10) mp + 3 else mp - 9).toInt()
            val y = (yoe + era * 400 + if (m <= 2) 1 else 0).toInt()
            return Triple(y, m, d)
        }

        /** The number of days since the epoch of a date. */
        private fun daysFromCivil(year: Int, month: Int, day: Int): Long {
            val y = (if (month <= 2) year - 1 else year).toLong()
            val era = (if (y >= 0) y else y - 399) / 400
            val yoe = y - era * 400
            val mp = (month + 9) % 12
            val doy = (153 * mp + 2) / 5 + day - 1
            val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
            return era * 146_097 + doe - 719_468
        }
    }
}
